import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import FolderCopyRoundedIcon from "@mui/icons-material/FolderCopyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import { useToast } from "../../context/ToastContext";
import { getWorkspaceTasks, updateWorkspaceTaskStatus } from "../../services/WorkspaceService";
import {
  TASK_STATUS,
  TASK_STATUS_ORDER,
  nextStatuses,
  statusMeta,
  initials,
  personName,
  timeAgo,
} from "./workspaceUtils";

const EMPTY_COLUMNS = {
  [TASK_STATUS.CREATED]: [],
  [TASK_STATUS.IN_PROGRESS]: [],
  [TASK_STATUS.FINISHED]: [],
};

const DRAG_MIME = "application/x-jex-task";

/** Saca la tarea de cualquier columna y la mete arriba de `toStatus`. */
function relocate(columns, taskId, taskObj, toStatus) {
  const next = {};
  for (const s of TASK_STATUS_ORDER) {
    next[s] = (columns[s] || []).filter((t) => t.id !== taskId);
  }
  next[toStatus] = [
    { ...taskObj, status: toStatus, status_changed_at: new Date().toISOString() },
    ...next[toStatus],
  ];
  return next;
}

function BoardCard({ task, isDark, onOpen, onMove, onEdit, onDelete, onDragStart, onDragEnd }) {
  const [anchor, setAnchor] = useState(null);
  const borderCol = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)";
  const assignee = personName(task.assigned_to);
  const creator = personName(task.created_by);
  const legalFileCount = Array.isArray(task.legal_file_codes) ? task.legal_file_codes.length : 0;
  const canMove = !!task.permissions?.can_change_status;
  const moves = canMove ? nextStatuses(task.status) : [];
  const canEdit = !!task.permissions?.can_edit;
  const canDelete = !!task.permissions?.can_delete;

  return (
    <Box
      draggable={canMove}
      onDragStart={(e) => {
        if (!canMove) return;
        e.dataTransfer.effectAllowed = "move";
        try {
          e.dataTransfer.setData(DRAG_MIME, String(task.id));
          e.dataTransfer.setData("text/plain", task.text || "");
        } catch (err) {
          /* algunos navegadores son quisquillosos con setData */
        }
        onDragStart(task);
      }}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(task.id)}
      sx={{
        p: 1.8,
        borderRadius: 3,
        cursor: canMove ? "grab" : "pointer",
        border: `1px solid ${borderCol}`,
        background: isDark ? "rgba(255,255,255,0.045)" : "#fff",
        boxShadow: isDark ? "0 8px 20px rgba(0,0,0,0.20)" : "0 6px 16px rgba(15,23,42,0.06)",
        transition: "border-color .15s ease, box-shadow .15s ease, transform .15s ease",
        "&:hover": {
          borderColor: "rgba(25,118,210,0.35)",
          boxShadow: "0 14px 32px rgba(0,0,0,0.12)",
        },
        "&:active": { cursor: canMove ? "grabbing" : "pointer" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
        {canMove && (
          <DragIndicatorRoundedIcon
            sx={{ fontSize: 16, mt: 0.2, opacity: 0.35, flexShrink: 0 }}
          />
        )}
        <Typography
          sx={{
            flex: 1,
            fontWeight: 700,
            fontSize: 13.8,
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            whiteSpace: "pre-wrap",
          }}
        >
          {task.text}
        </Typography>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchor(e.currentTarget);
          }}
          sx={{ width: 26, height: 26, mt: -0.4, flexShrink: 0 }}
        >
          <MoreVertRoundedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mt: 1.4,
        }}
      >
        <Tooltip title={assignee ? `Asignada a ${assignee}` : "Sin asignar"}>
          <Avatar
            sx={{ width: 24, height: 24, fontSize: 10.5, fontWeight: 800, bgcolor: "primary.main", color: "#fff" }}
          >
            {initials(assignee) || "?"}
          </Avatar>
        </Tooltip>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          {legalFileCount > 0 && (
            <Chip
              size="small"
              icon={<FolderCopyRoundedIcon sx={{ fontSize: "13px !important" }} />}
              label={legalFileCount}
              sx={{
                height: 22,
                borderRadius: 999,
                bgcolor: "rgba(25,118,210,0.10)",
                color: "primary.main",
                "& .MuiChip-label": { px: 0.8, fontWeight: 800, fontSize: 11.5 },
              }}
            />
          )}
          <Tooltip title={task.created_at ? new Date(task.created_at).toLocaleString() : ""}>
            <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.6 }}>
              {timeAgo(task.status_changed_at || task.created_at)}
            </Typography>
          </Tooltip>
        </Box>
      </Box>

      {creator && creator !== assignee && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.6, opacity: 0.55, fontWeight: 600 }}>
          Creada por {creator}
        </Typography>
      )}

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onOpen(task.id);
          }}
        >
          <ListItemIcon>
            <OpenInNewRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Ver detalle</ListItemText>
        </MenuItem>

        {moves.map((next) => {
          const meta = statusMeta(next);
          const reopen = next === TASK_STATUS.IN_PROGRESS && task.status === TASK_STATUS.FINISHED;
          return (
            <MenuItem
              key={next}
              onClick={() => {
                setAnchor(null);
                onMove(task, next);
              }}
            >
              <ListItemIcon>
                {reopen ? <ReplayRoundedIcon fontSize="small" /> : <ArrowForwardRoundedIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{reopen ? "Reabrir" : `Mover a "${meta.single}"`}</ListItemText>
            </MenuItem>
          );
        })}

        {canEdit && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              onEdit?.(task);
            }}
          >
            <ListItemIcon>
              <EditRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              onDelete?.(task);
            }}
          >
            <ListItemIcon>
              <DeleteRoundedIcon fontSize="small" sx={{ color: "#d32f2f" }} />
            </ListItemIcon>
            <ListItemText sx={{ color: "#d32f2f" }}>Eliminar</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

function Column({ status, tasks, isDark, isDropTarget, canDrop, onOpen, onMove, onEdit, onDelete, onCreate, cardHandlers, onDragOver, onDragLeave, onDrop }) {
  const meta = statusMeta(status);
  return (
    <Box
      onDragOver={(e) => onDragOver(e, status)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, status)}
      sx={{
        flexGrow: 1,
        flexBasis: 0,
        minWidth: 280,
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        p: 1.2,
        transition: "background-color .15s ease, border-color .15s ease",
        bgcolor: isDropTarget
          ? canDrop
            ? "rgba(25,118,210,0.10)"
            : "rgba(211,47,47,0.08)"
          : isDark
            ? "rgba(255,255,255,0.03)"
            : "rgba(0,0,0,0.025)",
        border: isDropTarget
          ? `1px dashed ${canDrop ? "rgba(25,118,210,0.5)" : "rgba(211,47,47,0.45)"}`
          : isDark
            ? "1px solid rgba(255,255,255,0.06)"
            : "1px solid rgba(0,0,0,0.05)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.6, py: 0.8 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: 999, bgcolor: meta.color }} />
        <Typography sx={{ fontWeight: 900, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.4 }}>
          {meta.label}
        </Typography>
        <Chip
          size="small"
          label={tasks.length}
          sx={{
            height: 20,
            borderRadius: 999,
            bgcolor: meta.bg,
            color: meta.color,
            "& .MuiChip-label": { px: 0.9, fontWeight: 800, fontSize: 11 },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mt: 1, minHeight: 60 }}>
        {tasks.length === 0 ? (
          <Typography variant="caption" sx={{ opacity: 0.45, px: 0.8, py: 1.4, fontWeight: 600 }}>
            {isDropTarget && canDrop ? "Soltá acá" : "Sin tareas en esta columna."}
          </Typography>
        ) : (
          tasks.map((task) => (
            <BoardCard
              key={task.id}
              task={task}
              isDark={isDark}
              onOpen={onOpen}
              onMove={onMove}
              onEdit={onEdit}
              onDelete={onDelete}
              {...cardHandlers}
            />
          ))
        )}

        {onCreate && (
          <Button
            fullWidth
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={onCreate}
            sx={{
              mt: 0.4,
              justifyContent: "flex-start",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              color: "text.secondary",
              "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" },
            }}
          >
            Crear tarea
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default function WorkspaceBoard({ refreshSignal = 0, onEdit, onDelete, onCreate }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [columns, setColumns] = useState(EMPTY_COLUMNS);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("all");

  const draggingRef = useRef(null); // { task, from }
  const [dragOverCol, setDragOverCol] = useState(null);
  const [dragValid, setDragValid] = useState(false);

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWorkspaceTasks({ board: 1 });
      setColumns({ ...EMPTY_COLUMNS, ...(res.data?.columns || {}) });
    } catch (error) {
      console.error("[WorkspaceBoard] Error al cargar el tablero:", error);
      showToast("No se pudo cargar el tablero de tareas.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard, refreshSignal]);

  const assignees = useMemo(() => {
    const byId = new Map();
    TASK_STATUS_ORDER.forEach((s) => {
      (columns[s] || []).forEach((t) => {
        if (t.assigned_to?.id && !byId.has(t.assigned_to.id)) {
          byId.set(t.assigned_to.id, t.assigned_to);
        }
      });
    });
    return Array.from(byId.values()).sort((a, b) => personName(a).localeCompare(personName(b)));
  }, [columns]);

  const filteredColumns = useMemo(() => {
    const term = search.trim().toLowerCase();
    const apply = (list) =>
      (list || []).filter((t) => {
        const matchText =
          !term ||
          (t.text || "").toLowerCase().includes(term) ||
          (t.legal_file_codes || []).some((c) => String(c).toLowerCase().includes(term));
        const matchAssignee =
          assigneeFilter === "all" || t.assigned_to?.id === Number(assigneeFilter);
        return matchText && matchAssignee;
      });
    return {
      [TASK_STATUS.CREATED]: apply(columns[TASK_STATUS.CREATED]),
      [TASK_STATUS.IN_PROGRESS]: apply(columns[TASK_STATUS.IN_PROGRESS]),
      [TASK_STATUS.FINISHED]: apply(columns[TASK_STATUS.FINISHED]),
    };
  }, [columns, search, assigneeFilter]);

  const handleMove = useCallback(
    async (task, nextStatus) => {
      if (task.status === nextStatus) return;
      setColumns((prev) => relocate(prev, task.id, task, nextStatus));
      try {
        await updateWorkspaceTaskStatus(task.id, { status: nextStatus });
        showToast("Estado actualizado.", "success");
      } catch (error) {
        setColumns((prev) => relocate(prev, task.id, task, task.status));
        showToast(
          error?.response?.data?.message || "No se pudo cambiar el estado de la tarea.",
          "error",
        );
      }
    },
    [showToast],
  );

  const handleOpen = (id) => navigate(`/workspace/${id}`);

  // ---- drag & drop ----
  const onCardDragStart = (task) => {
    draggingRef.current = { task, from: task.status };
  };
  const onCardDragEnd = () => {
    draggingRef.current = null;
    setDragOverCol(null);
    setDragValid(false);
  };
  const onColumnDragOver = (e, status) => {
    const drag = draggingRef.current;
    if (!drag) return;
    e.preventDefault();
    const valid = drag.from !== status && nextStatuses(drag.from).includes(status);
    e.dataTransfer.dropEffect = valid ? "move" : "none";
    if (dragOverCol !== status || dragValid !== valid) {
      setDragOverCol(status);
      setDragValid(valid);
    }
  };
  const onColumnDragLeave = () => {
    // no limpiamos acá para evitar parpadeo entre hijos; el dragOver del destino corrige
  };
  const onColumnDrop = (e, status) => {
    e.preventDefault();
    const drag = draggingRef.current;
    draggingRef.current = null;
    setDragOverCol(null);
    setDragValid(false);
    if (!drag) return;
    const { task, from } = drag;
    if (from === status) return;
    if (!task.permissions?.can_change_status) {
      showToast("No tenés permiso para cambiar el estado de esta tarea.", "error");
      return;
    }
    if (!nextStatuses(from).includes(status)) {
      showToast(
        `No se puede pasar de "${statusMeta(from).single}" a "${statusMeta(status).single}".`,
        "error",
      );
      return;
    }
    handleMove(task, status);
  };

  const cardHandlers = { onDragStart: onCardDragStart, onDragEnd: onCardDragEnd };

  if (loading) {
    return (
      <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 1 }}>
        {TASK_STATUS_ORDER.map((s) => (
          <Box key={s} sx={{ flexGrow: 1, flexBasis: 0, minWidth: 280 }}>
            <Skeleton variant="text" width={140} height={26} />
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: 3, mt: 1.2 }} />
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2, justifyContent: "flex-end" }}>
        <TextField
          size="small"
          placeholder="Buscar en el tablero..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240, flex: "0 1 320px" }}
        />
        <TextField
          size="small"
          select
          SelectProps={{ native: true }}
          label="Responsable"
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <option value="all">Todos</option>
          {assignees.map((u) => (
            <option key={u.id} value={u.id}>
              {personName(u)}
            </option>
          ))}
        </TextField>
      </Box>

      <Box sx={{ overflowX: "auto", pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          {TASK_STATUS_ORDER.map((status) => (
            <Column
              key={status}
              status={status}
              tasks={filteredColumns[status]}
              isDark={isDark}
              isDropTarget={dragOverCol === status}
              canDrop={dragValid}
              onOpen={handleOpen}
              onMove={handleMove}
              onEdit={onEdit}
              onDelete={onDelete}
              onCreate={status === TASK_STATUS.CREATED ? onCreate : undefined}
              cardHandlers={cardHandlers}
              onDragOver={onColumnDragOver}
              onDragLeave={onColumnDragLeave}
              onDrop={onColumnDrop}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
